/**
 * Service for handling "Script New..." context menu actions.
 * Opens a new query tab with a template snippet for creating new database objects.
 */

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { getScriptProvider } from '../../scripting';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Context for generating new object scripts
 */
interface NewObjectScriptContext {
  connectionId: string;
  database: string;
  schema: string;
}

/**
 * Get default schema for an engine
 */
function getDefaultSchema(engine: string): string {
  if (engine === 'pgsql') return 'public';
  if (engine === 'sqlite' || engine === 'mysql') return '';
  if (engine === 'oracle') return 'JAMSQL'; // Fallback; actual schema comes from node context
  return 'dbo';
}

/**
 * Open a new query tab with a snippet template
 */
function openSnippetTab(
  title: string,
  snippet: string,
  context: NewObjectScriptContext
): void {
  const { connectionId, database } = context;

  // Get connection info (supports both saved connections and browser mode)
  const { activeConnection, connection } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${title}-${queryNumber}`,
    type: 'query',
    content: {
      query: '',
      insertSnippetOnMount: snippet,
    },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
}

/**
 * Handle "Script New Table..." action
 */
export function handleScriptNewTable(context: NewObjectScriptContext): void {
  const { connectionId } = context;

  // Get connection info (supports both saved connections and browser mode)
  const { connection, engine } = getConnectionById(connectionId);

  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const template = provider.generateNewTableTemplate({
      database: context.database,
      schema: context.schema,
    });

    openSnippetTab('New Table', template, context);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate template',
    });
  }
}

/**
 * Handle "Script New View..." action
 */
export function handleScriptNewView(context: NewObjectScriptContext): void {
  const { connectionId } = context;

  // Get connection info (supports both saved connections and browser mode)
  const { connection, engine } = getConnectionById(connectionId);

  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const template = provider.generateNewViewTemplate({
      database: context.database,
      schema: context.schema,
    });

    openSnippetTab('New View', template, context);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate template',
    });
  }
}

/**
 * Handle "Script New Stored Procedure..." action
 */
export function handleScriptNewProcedure(context: NewObjectScriptContext): void {
  const { connectionId } = context;

  // Get connection info (supports both saved connections and browser mode)
  const { connection, engine } = getConnectionById(connectionId);

  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const template = provider.generateNewProcedureTemplate({
      database: context.database,
      schema: context.schema,
    });

    openSnippetTab('New Procedure', template, context);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate template',
    });
  }
}

/**
 * Handle "Script New Function..." action
 */
export function handleScriptNewFunction(context: NewObjectScriptContext): void {
  const { connectionId } = context;

  // Get connection info (supports both saved connections and browser mode)
  const { connection, engine } = getConnectionById(connectionId);

  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const template = provider.generateNewFunctionTemplate({
      database: context.database,
      schema: context.schema,
    });

    openSnippetTab('New Function', template, context);
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate template',
    });
  }
}

/**
 * Handle "Script New Database..." action
 */
export function handleScriptNewDatabase(connectionId: string): void {
  // Get connection info (supports both saved connections and browser mode)
  const { activeConnection, connection, engine } = getConnectionById(connectionId);

  if (!connection || !activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  try {
    const provider = getScriptProvider(engine);
    const template = provider.generateNewDatabaseTemplate();

    const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
    const queryNumber = getNextQueryNumber();

    addTab({
      title: `New Database-${queryNumber}`,
      type: 'query',
      content: {
        query: '',
        insertSnippetOnMount: template,
      },
      connectionInfo: {
        connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: 'master', // Use master for creating databases
      },
    });
  } catch (error) {
    toast.error({
      title: 'Script Generation Error',
      description: error instanceof Error ? error.message : 'Failed to generate template',
    });
  }
}

/**
 * Handle "Script New Package..." action (Oracle-specific)
 */
export function handleScriptNewPackage(context: NewObjectScriptContext): void {
  const { connection, engine } = getConnectionById(context.connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const provider = getScriptProvider(engine);
  if (!provider.generateNewPackageTemplate) return;
  const template = provider.generateNewPackageTemplate({
    database: context.database,
    schema: context.schema,
  });
  openSnippetTab('New Package', template, context);
}

/**
 * Handle "Script New Sequence..." action (Oracle-specific)
 * Opens the form-based CreateSequenceDialog instead of a template.
 */
export function handleScriptNewSequence(context: NewObjectScriptContext): void {
  const { connection, engine } = getConnectionById(context.connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  // Oracle: open form-based dialog
  if (engine === 'oracle') {
    import('../../workspace/oracle-object-detail/store/oracleObjectDialogStore').then(
      ({ useOracleObjectDialogStore }) => {
        useOracleObjectDialogStore.getState().openCreateSequence({
          connectionId: context.connectionId,
          schema: context.schema,
          database: context.database,
        });
      },
    );
    return;
  }
  const provider = getScriptProvider(engine);
  if (!provider.generateNewSequenceTemplate) return;
  const template = provider.generateNewSequenceTemplate({
    database: context.database,
    schema: context.schema,
  });
  openSnippetTab('New Sequence', template, context);
}

/**
 * Handle "Script New Synonym..." action (Oracle-specific)
 * Opens the form-based CreateSynonymDialog instead of a template.
 */
export function handleScriptNewSynonym(context: NewObjectScriptContext): void {
  const { connection, engine } = getConnectionById(context.connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  // Oracle: open form-based dialog
  if (engine === 'oracle') {
    import('../../workspace/oracle-object-detail/store/oracleObjectDialogStore').then(
      ({ useOracleObjectDialogStore }) => {
        useOracleObjectDialogStore.getState().openCreateSynonym({
          connectionId: context.connectionId,
          schema: context.schema,
          database: context.database,
        });
      },
    );
    return;
  }
  const provider = getScriptProvider(engine);
  if (!provider.generateNewSynonymTemplate) return;
  const template = provider.generateNewSynonymTemplate({
    database: context.database,
    schema: context.schema,
  });
  openSnippetTab('New Synonym', template, context);
}

/**
 * Handle "New DB Link..." action (Oracle-specific)
 * Opens the form-based CreateDbLinkDialog.
 */
export function handleScriptNewDbLink(context: NewObjectScriptContext): void {
  const { connection } = getConnectionById(context.connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  import('../../workspace/oracle-object-detail/store/oracleObjectDialogStore').then(
    ({ useOracleObjectDialogStore }) => {
      useOracleObjectDialogStore.getState().openCreateDbLink({
        connectionId: context.connectionId,
        schema: context.schema,
        database: context.database,
      });
    },
  );
}

/**
 * Handle "Script New Trigger..." action (Oracle-specific)
 */
export function handleScriptNewTrigger(context: NewObjectScriptContext): void {
  const { connection, engine } = getConnectionById(context.connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const provider = getScriptProvider(engine);
  if (!provider.generateNewTriggerTemplate) return;
  const template = provider.generateNewTriggerTemplate({
    database: context.database,
    schema: context.schema,
  });
  openSnippetTab('New Trigger', template, context);
}

/**
 * Create context from folder node metadata
 */
export function createContextFromFolderMetadata(
  connectionId: string,
  database: string | undefined,
  engine: string
): NewObjectScriptContext {
  // Get connection info (supports both saved connections and browser mode)
  const { activeConnection } = getConnectionById(connectionId);

  const db = database || activeConnection?.connection?.database || '';

  return {
    connectionId,
    database: db,
    // MySQL and Oracle: database/schema name is the schema qualifier
    schema: (engine === 'mysql' || engine === 'oracle') ? db : getDefaultSchema(engine),
  };
}

/**
 * Create context from database node
 */
export function createContextFromDatabaseNode(
  connectionId: string,
  databaseName: string,
  engine: string
): NewObjectScriptContext {
  return {
    connectionId,
    database: databaseName,
    // MySQL and Oracle: database/schema name is the schema qualifier
    schema: (engine === 'mysql' || engine === 'oracle') ? databaseName : getDefaultSchema(engine),
  };
}
